import { Color3, type Mesh, MeshBuilder, StandardMaterial, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, Slot, hv } from "@rivalwatch/shared";
import { PK, V, ZK, FX } from "../../../../shared/src/heroes/roster/black-window.ts";
import { baked } from "../../audio/bake.ts";
import { bell, brass, click, clinks, drive, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero, ProjectileFrame, ZoneFrame } from "../types.ts";

type Gear = { rifle: Mesh; scope: Mesh; barrel: Mesh; stock: Mesh; batonL: Mesh; batonR: Mesh; mark: Mesh; charge: Mesh };

const rifleHip: Cue = baked("black-window:rifle-hip", () => trim(4.0, (o) => {
  const p = 1 + (Math.random() * 2 - 1) * 0.025;
  click(o, 0.55, 3600, 0.004);
  thump(o, 0.5, 135 * p, 62, 0.1);
  noise(drive(o, 1.8), { color: "pink", type: "bandpass", f: 1800 * p, f1: 900, q: 1.0, gain: 0.42, d: 0.12 });
  clinks(later(o, 0.08), 2, 0.045, 0.08, 1800, 3600);
  return 0.34;
}), { variants: 5, jitter: 0.015 });

const rifleScoped: Cue = baked("black-window:rifle-scoped", () => trim(4, (o) => {
  click(o, 0.9, 4300, 0.005);
  thump(o, 0.72, 105, 42, 0.2);
  noise(drive(o, 2.3), { color: "pink", type: "lowpass", f: 2600, f1: 650, gain: 0.58, d: 0.24 });
  fm(later(o, 0.03), { f: 1700, ratio: 1.51, index: 1.8, index1: 0.1, gain: 0.08, d: 0.36 });
  return 0.62;
}), { variants: 4, jitter: 0.01 });

const plasmaCharge: Cue = trim(8.5, (o) => {
  tone(o, { type: "sawtooth", f: 150, f1: 980, glide: 0.55, gain: 0.13, a: 0.08, d: 0.38, lp: 2200 });
  noise(o, { color: "crackle", type: "bandpass", f: 2600, f1: 5200, q: 0.8, gain: 0.45, a: 0.04, d: 0.5 });
  bell(later(o, 0.22), 1174.6, 0.35, 0.7);
  return 0.9;
});
const fleetCue: Cue = trim(8, (o) => {
  whoosh(o, 0.9, 420, 2100, 0.35, 0, 1.1);
  thump(o, 0.35, 170, 70, 0.09);
  return 0.5;
});
const kickCue: Cue = trim(0, (o) => {
  whoosh(o, 0.7, 650, 2800, 0.16);
  thump(later(o, 0.08), 0.75, 160, 54, 0.18);
  fm(later(o, 0.06), { f: 520, ratio: 2.3, index: 4, index1: 0, gain: 0.16, d: 0.2 });
  return 0.45;
});
const dropCue: Cue = trim(7, (o) => {
  whoosh(o, 0.7, 1000, 280, 0.24);
  thump(later(o, 0.16), 0.9, 120, 38, 0.25);
  noise(later(o, 0.13), { color: "crackle", type: "bandpass", f: 1900, gain: 0.32, d: 0.22 });
  return 0.65;
});
const reloadCue = (seconds: number): Cue => trim(10, (o) => {
  click(o, 0.28, 1600, 0.01, seconds * 0.18);
  clinks(o, 4, 0.065, seconds * 0.55, 1000, 3100);
  click(o, 0.38, 2400, 0.008, seconds * 0.78);
  return seconds + 0.15;
});
const plasmaBoom = (radius: number): Cue => trim(8, (o) => {
  click(o, 0.65, 1800, 0.012);
  thump(o, 1.05, 86, 28, 0.55);
  noise(drive(o, 2.4), { color: "pink", type: "lowpass", f: 3200, f1: 260, gain: 0.8, d: 0.48 + radius * 0.04 });
  fm(later(o, 0.05), { f: 92, ratio: 2.02, index: 7, index1: 0.6, gain: 0.26, d: 0.7 });
  return 1.15;
});
const theme = {
  bpm: 104,
  octave: 0,
  voice: brass,
  double: (o, f, d, v) => void bell(o, f * 2, d * 0.7, v * 0.35),
  notes: [[0, 0.5], [2, 0.5], [7, 1], [6, 0.5], [4, 1.5]],
} satisfies ClientHero["audio"]["theme"];

const icon = {
  rifle: "M4 13h9M13 10l7-3M13 16l7 3M6 10v6M3 12h3",
  scope: "M4 12a8 8 0 0 1 16 0a8 8 0 0 1-16 0M12 4v16M4 12h16",
  fleet: "M5 17c5-1 9-4 13-9M6 7h7M4 11h8M3 15h7",
  kick: "M6 6l5 5M11 11l8-2M11 11l-3 7M15 9l4 5",
  drop: "M12 3v12M8 11l4 4 4-4M5 21h14",
  plasma: "M12 3c5 3 7 8 1 18M12 3c-5 3-7 8-1 18M5 12h14",
};

const hero: ClientHero<Gear> = {
  id: HeroId.BlackWindow,
  bulk: 0.92,
  shape: { head: 0.95, torso: 0.88, arms: 0.9, legs: 1.07 },
  gear(g) {
    const { scene, add, rig, toon, glow, R, H } = g;
    const rifle = add(MeshBuilder.CreateBox("ledger-rifle", { width: 0.13, height: 0.16, depth: H * 0.7 }, scene), rig.handR, toon("#10131a"));
    rifle.position.z = -H * 0.3;
    const barrel = add(MeshBuilder.CreateCylinder("ledger-barrel", { diameter: 0.07, height: H * 0.38, tessellation: 10 }, scene), rig.handR, toon("#090a0f"));
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = -H * 0.72;
    const scope = add(MeshBuilder.CreateCylinder("rifle-scope", { diameter: 0.11, height: 0.36, tessellation: 12 }, scene), rig.handR, glow("#f28ab0", 0.55));
    scope.rotation.x = Math.PI / 2;
    scope.position.set(0, 0.12, -0.34);
    const stock = add(MeshBuilder.CreateBox("rifle-stock", { width: 0.18, height: 0.19, depth: 0.22 }, scene), rig.handR, toon("#1b1018"));
    stock.position.z = 0.12;
    const batonL = add(MeshBuilder.CreateCylinder("back-baton-l", { diameter: 0.055, height: 0.9, tessellation: 8 }, scene), rig.torso, glow("#ff4fa0"));
    batonL.rotation.x = 0.65;
    batonL.rotation.z = 0.35;
    batonL.position.set(-R * 0.45, 0.1, R * 0.65);
    const batonR = add(MeshBuilder.CreateCylinder("back-baton-r", { diameter: 0.055, height: 0.9, tessellation: 8 }, scene), rig.torso, glow("#ff4fa0"));
    batonR.rotation.x = 0.65;
    batonR.rotation.z = -0.35;
    batonR.position.set(R * 0.45, 0.1, R * 0.65);
    const mark = add(MeshBuilder.CreateTorus("red-window-belt", { diameter: R * 0.78, thickness: 0.024, tessellation: 18 }, scene), rig.torso, glow("#c81e5a", 0.65));
    mark.rotation.x = Math.PI / 2;
    mark.position.set(0, -0.22, -R * 0.57);
    const charge = add(MeshBuilder.CreateSphere("plasma-charge", { diameter: 0.22, segments: 12 }, scene), rig.handL, glow("#ff4fa0", 0.9));
    charge.position.set(0, 0, -0.3);
    charge.setEnabled(false);
    return { rifle, scope, barrel, stock, batonL, batonR, mark, charge };
  },
  animate(gear, f) {
    const scoped = f.v[V.scoped] > 0;
    const charge = f.v[V.plasmaCharge] ?? 0;
    gear.scope.visibility = scoped ? 1 : 0.45;
    gear.charge.setEnabled(charge > 0.02);
    gear.charge.scaling.setAll(0.7 + charge * 2.2);
    gear.mark.rotation.z += f.dt * (charge > 0 ? 7 : 1.5);
    gear.batonL.visibility = f.action === 3 || f.action === 4 ? 1 : 0.75;
    gear.batonR.visibility = gear.batonL.visibility;
    if (f.moveKind === 5 || f.moveKind === 4) return "attack-kick-right";
    if (charge > 0) return "holding-both";
    return scoped ? "holding-right" : undefined;
  },
  hud(c, eid) {
    return [
      { label: "Fleet Foot", value: hv(c, eid, V.fleet) / 120, color: "#f28ab0" },
      { label: "Plasma", value: hv(c, eid, V.plasmaCharge), color: "#ff4fa0" },
    ];
  },
  scope(c, eid, buttons, alive) {
    if (!alive) return undefined;
    return buttons & Btn.Secondary ? 0.42 : undefined;
  },
  icons: { [Slot.Primary]: icon.rifle, [Slot.Secondary]: icon.scope, [Slot.Ability1]: icon.fleet, [Slot.Ability2]: icon.kick, [Slot.Ability3]: icon.drop, [Slot.Ultimate]: icon.plasma, passive: "M6 18l12-12M6 6l12 12" },
  projectiles: {
    [PK.Plasma](scene, f: ProjectileFrame) {
      const core = MeshBuilder.CreateSphere("window-plasma", { diameter: 0.45, segments: 12 }, scene);
      const mat = new StandardMaterial("window-plasma-mat", scene);
      mat.emissiveColor = Color3.FromHexString(f.rel === "foe" ? "#ff4fa0" : "#f28ab0");
      mat.disableLighting = true;
      core.material = mat;
      return {
        gear: core,
        update(mesh: Mesh, frame: ProjectileFrame) {
          mesh.position = new Vector3(frame.pos.x, frame.pos.y, frame.pos.z);
          mesh.scaling.setAll(1 + Math.sin(frame.age * 30) * 0.18);
        },
        dispose: () => core.dispose(),
      };
    },
  },
  zones: {
    [ZK.PlasmaPool](scene, _f: ZoneFrame) {
      const disc = MeshBuilder.CreateCylinder("plasma-pool", { diameter: 2, height: 0.045, tessellation: 32 }, scene);
      const mat = new StandardMaterial("plasma-pool-mat", scene);
      mat.emissiveColor = Color3.FromHexString("#ff4fa0");
      mat.alpha = 0.34;
      mat.disableLighting = true;
      disc.material = mat;
      return {
        gear: disc,
        update(mesh: Mesh, frame: ZoneFrame) {
          mesh.position.set(frame.pos.x, frame.pos.y + 0.03, frame.pos.z);
          const pulse = frame.radius * (1 + Math.sin(frame.age * 9) * 0.04);
          mesh.scaling.set(pulse, 1, pulse);
          mat.alpha = 0.28 + Math.sin(frame.age * 12) * 0.06;
        },
        dispose: () => disc.dispose(),
      };
    },
  },
  fx: {
    [FX.Rifle]: {
      tracer(c, from, to) {
        const a = new Vector3(from.x, from.y, from.z);
        const b = new Vector3(to.x, to.y, to.z);
        const beam = MeshBuilder.CreateBox("window-tracer", { width: 0.035, height: 0.035, depth: Vector3.Distance(a, b) }, c.scene);
        beam.material = c.glow("#f28ab0", 0.9);
        beam.position = Vector3.Lerp(a, b, 0.5);
        beam.lookAt(b);
        c.timed(beam, 0.08, (t, m) => m.scaling.set(1 - t, 1 - t, 1));
      },
    },
    [FX.Plasma]: {
      explosion(c, pos, radius) {
        const m = MeshBuilder.CreateSphere("plasma-boom", { diameter: 2, segments: 18 }, c.scene);
        m.material = c.glow("#ff4fa0", 0.45);
        m.position.set(pos.x, pos.y + 0.4, pos.z);
        c.timed(m, 0.5, (t, mesh) => mesh.scaling.setAll(radius * (0.2 + t * 0.8)));
      },
    },
  },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.scoped ? "black-window:scoped" : "black-window:hip", cue: ctx?.scoped ? rifleScoped : rifleHip }),
    reload: reloadCue,
    ability: { [Slot.Secondary]: plasmaCharge, [Slot.Ability1]: fleetCue, [Slot.Ability2]: kickCue, [Slot.Ability3]: dropCue, [Slot.Ultimate]: plasmaCharge },
    explosions: { [FX.Plasma]: plasmaBoom },
    impacts: { [FX.Rifle]: trim(6, (o) => (click(o, 0.24, 5200, 0.004), fm(o, { f: 2600, ratio: 1.7, index: 1, gain: 0.06, d: 0.1 }), 0.16)) },
    theme,
    themeTrim: { heroic: 0.2, hostile: -6.5, tease: -1.4 },
  },
};

export default hero;
